import { useEffect, useRef } from 'react';

/**
 * Hook that adds the `in-view` class to an element when it scrolls into the viewport.
 * Uses IntersectionObserver — observes once, then stops.
 *
 * Usage:
 *   const ref = useAnimateOnScroll();
 *   <div ref={ref} className="slide-up">...</div>
 */
export function useAnimateOnScroll(threshold = 0.1) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > threshold) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold]);

  return ref;
}
